import React, { useMemo } from 'react';
import { Icon, TableColumn, Table, Tag, OptionsMenu } from '@scale/scaleui';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import {
  EmailDripCampaignType,
  PodGroup,
  CommsType,
  EmailDripEventType,
} from '@scale/shared-types';
import {
  deleteCampaign,
  getEmailDripCampaignEvents,
  pauseCampaign,
  transformCSVUrl,
} from 'react-scale/corp/lib/api/growth';
import SAlert from 'react-s-alert';
import { useQueryClient, useMutation } from 'react-query';
import {
  ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
  YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
  EmailDripCampaignListTab,
} from 'react-scale/components/GrowthCenter/constants';
import { PreviewMetricsModal } from './PreviewMetricsModal';
import { HistoryTableModal } from 'react-scale/components/GrowthCenter/components/HistoryTableModal';

const CAMPAIGN_TYPE_TAG = {
  // Note: The "warning" and "danger" classnames are used solely for color styling purposes.
  [EmailDripCampaignType.OCP]: <Tag variant="secondary">OCP</Tag>,
  [EmailDripCampaignType.ACP]: <Tag variant="secondary">ACP</Tag>,
  [EmailDripCampaignType.Greenhouse]: <Tag variant="success">Greenhouse</Tag>,
  [EmailDripCampaignType.CSV]: <Tag variant="info">CSV</Tag>,
  [EmailDripCampaignType.WorkerTeam]: (
    <Tag variant="white" className="bg-warning-50">
      Worker Team
    </Tag>
  ),
  [EmailDripCampaignType.Project]: (
    <Tag variant="white" className="bg-danger-50">
      Project
    </Tag>
  ),
  [EmailDripCampaignType.EventDriven]: <Tag variant="info">Event Driven</Tag>,
};

export const POD_GROUP_TAG = {
  [PodGroup.Specialist]: <Tag variant="neutral">Specialists</Tag>,
  [PodGroup.Generalist]: <Tag variant="neutral">Generalists</Tag>,
  [PodGroup.Coder]: <Tag variant="neutral">Coders</Tag>,
  [PodGroup.Language]: <Tag variant="neutral">Languages</Tag>,
  [PodGroup.None]: <Tag variant="neutral">None</Tag>,
};

const COMMS_TYPE_TAG = {
  [CommsType.Broadcast]: (
    <Tag variant="neutral" icon="bullhorn">
      Broadcast
    </Tag>
  ),
  [CommsType.Campaign]: (
    <Tag variant="neutral" icon="arrows-rotate">
      Campaign
    </Tag>
  ),
  [CommsType.EventDriven]: (
    <Tag variant="neutral" icon="bolt">
      Event Driven
    </Tag>
  ),
};

export const downloadCSV = async (url: string, name: string) => {
  const csvData = await (await fetch(url)).text();
  const blob = new Blob([csvData], { type: 'text/csv' });

  // Create a download link and click it
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${name}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

const getDuplicateName = (name: string, allData: object[]) => {
  let copyName = `${name} (Copy)`,
    nameExists = true;
  for (let copyNumber = 1; nameExists; copyNumber++) {
    if (copyNumber > 1) copyName = `${name} (Copy ${copyNumber})`;
    const duplicateCampaign = allData.some((campaign: any) => campaign.name === copyName);
    if (!duplicateCampaign) nameExists = false;
  }
  return copyName;
};

const EmailDripCampaignEventDrivenCommunicationsColumns: TableColumn[] = [
  {
    id: 'lifecycleCommEventType',
    header: 'Event Type',
    enableSorting: false,
    cell: function EventTypeCell(props: any) {
      const eventType = props.getValue();
      return (
        <span>
          {String(eventType || '')
            .replace(/-/g, ' ')
            .replace(/^\w/, c => c.toUpperCase())}
        </span>
      );
    },
  },
  {
    id: 'category',
    header: 'Category',
    enableSorting: false,
    cell: function CategoryCell(props: any) {
      const category = props.getValue();
      return (
        <Tag variant="neutral">
          {String(category || '')
            .replace(/-/g, ' ')
            .replace(/^\w/, c => c.toUpperCase())}
        </Tag>
      );
    },
  },
  {
    id: 'priority',
    header: 'Priority',
    enableSorting: false,
  },
  {
    id: 'channels',
    header: 'Channels',
    enableSorting: false,
  },
  {
    id: 'deliverySchedule',
    header: 'Delivery Schedule',
    enableSorting: false,
    cell: function DeliveryScheduleCell(props: any) {
      const deliverySchedule = props.getValue();

      if (!deliverySchedule || typeof deliverySchedule !== 'object') {
        return <div className="text-gray-500">No schedule</div>;
      }

      const scheduleEntries = Object.entries(deliverySchedule);

      if (scheduleEntries.length === 0) {
        return <div className="text-gray-500">No schedule</div>;
      }

      return (
        <div className="space-y-1">
          {scheduleEntries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-700">Day {key}:</span>
              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                {value as unknown as React.ReactNode}
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    id: 'smsSchedule',
    header: 'SMS Schedule',
    enableSorting: false,
    cell: function SmsScheduleCell(props: any) {
      const smsSchedule = props.getValue();

      if (!smsSchedule || typeof smsSchedule !== 'object') {
        return <div className="text-gray-500">No SMS schedule</div>;
      }

      const scheduleEntries = Object.entries(smsSchedule);

      if (scheduleEntries.length === 0) {
        return <div className="text-gray-500">No SMS schedule</div>;
      }

      return (
        <div className="space-y-1">
          {scheduleEntries.map(([key, value]) => (
            <div key={key} className="flex items-start gap-2 text-sm">
              <span className="font-medium text-gray-700 shrink-0">Day {key}:</span>
              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-words max-w-md whitespace-normal">
                {value as string}
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    id: 'metrics',
    header: 'Metrics',
    enableSorting: false,
    cell: function MetricsCell(props: any) {
      const emailTemplateId = props.getValue();
      return (
        <div className="w-full flex justify-center">
          <PreviewMetricsModal emailTemplateId={emailTemplateId} />
        </div>
      );
    },
  },
];

const EmailDripCampaignDataColumns: TableColumn[] = [
  {
    id: 'name',
    header: 'Name',
    enableSorting: true,
  },
  {
    id: 'commsType',
    header: 'Type',
    enableSorting: false,
    cell: function TypeCell(props: any) {
      const type = props.getValue();
      return <div>{!!type && COMMS_TYPE_TAG[type as CommsType]}</div>;
    },
  },
  {
    id: 'source',
    header: 'Source',
    enableSorting: false,
    cell: function TypeCell(props: any) {
      const type = props.getValue()?.type;
      const id = props.getValue()?.id;
      const name = props.getValue()?.name;

      return (
        <div className="pt-1.5">
          <div>{CAMPAIGN_TYPE_TAG[type as EmailDripCampaignType]}</div>
          <Link
            href={
              type === EmailDripCampaignType.OCP
                ? `/corp/growth-center/onboarding-control-panel/${id}`
                : type === EmailDripCampaignType.Greenhouse
                ? `https://app5.greenhouse.io/sdash/${id}`
                : `/corp/lookup/${id}`
            }
          >
            <a
              className="flex items-center ml-1.5 mt-0.5 -mr-4"
              target="_blank"
              rel="noopener noreferrer"
              onClick={async e => {
                if (type === EmailDripCampaignType.CSV) {
                  e.preventDefault();
                  const url = await transformCSVUrl(id);
                  await downloadCSV(url, name);
                }
              }}
            >
              <div className="text-xs truncate max-w-[200px]">{name}</div>
              {type !== EmailDripCampaignType.CSV ? (
                <Icon icon="external-link-alt" className="text-xs ml-2" size="sm" />
              ) : (
                <Icon icon="arrow-down-to-bracket" className="text-xs ml-2" size="sm" />
              )}
            </a>
          </Link>
        </div>
      );
    },
  },
  {
    id: 'pod',
    header: 'Pod',
    cell: function PodCell(props: any) {
      const pod = props.getValue();
      return (pod && <div>{POD_GROUP_TAG[pod as PodGroup]}</div>) || <div></div>;
    },
  },
  {
    id: 'creator',
    header: 'Creator',
    enableSorting: false,
  },
  {
    id: 'createAt',
    header: 'Created At',
    enableSorting: true,
    cell: function CreatedAtCell(props: any) {
      const createdAt = props.getValue();
      return new Date(createdAt).toLocaleString().split(',')[0];
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: function StatusCell(props: any) {
      const activeDripCount = props.getValue();
      return activeDripCount > 0 ? (
        <div className="pt-0.5">
          <div>
            <Tag variant="success">Active</Tag>
          </div>
          <div className="text-xs mx-1.5 mt-1">
            {activeDripCount} active {activeDripCount > 1 ? 'emails' : 'email'}
          </div>
        </div>
      ) : (
        <div className="pt-0.5">
          <div>
            <Tag variant="neutral">Paused</Tag>
          </div>

          <div className="text-xs mx-1.5 mt-1">No active emails</div>
        </div>
      );
    },
  },
  {
    id: 'metrics',
    header: 'Metrics',
    cell: function MetricsCell(props: any) {
      const campaignId = props.getValue();
      return (
        <div className="w-full flex justify-center">
          <PreviewMetricsModal campaignId={campaignId} />
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: 'history',
    header: 'History',
    enableSorting: false,
  },
  {
    id: 'actions',
    header: 'Actions',
    enableSorting: false,
  },
];

interface EmailDripCampaignTableProps {
  isLoading: boolean;
  data: object[];
  allData: object[];
  currentTab: string;
  lifecycleCommTemplates: object[];
}

export const EmailDripCampaignTable: React.FC<EmailDripCampaignTableProps> = ({
  isLoading,
  data,
  allData,
  currentTab,
  lifecycleCommTemplates,
}: EmailDripCampaignTableProps) => {
  const deleteCampaignMutation = useMutation(
    (campaignId: string) => {
      return deleteCampaign(campaignId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
        queryClient.invalidateQueries([YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
        SAlert.success('Campaign deleted successfully');
      },
    },
  );

  const pauseCampaignMutation = useMutation(
    (campaignId: string) => {
      return pauseCampaign(campaignId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
        queryClient.invalidateQueries([YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
        SAlert.success('Campaign paused successfully');
      },
    },
  );

  const queryClient = useQueryClient();
  const processedData = useMemo(() => {
    if (currentTab === EmailDripCampaignListTab.LifecycleCommunications) {
      return lifecycleCommTemplates.map((row: any) => ({
        lifecycleCommEventType: row.lifecycleCommEventType || '',
        category: row.lifecycleCommMetadata?.category || '',
        deliverySchedule: row.lifecycleCommMetadata?.deliverySchedule || '',
        priority: row.lifecycleCommMetadata?.priority || '',
        smsSchedule: row.lifecycleCommMetadata?.smsSchedule || {},
        metrics: row.templateId,
        channels: row.lifecycleCommMetadata?.channels?.join(', ') || '',
      }));
    } else {
      return data.map((row: any) => ({
        name: (
          <Link
            href={`${Routes.CommunicationSetup}/${row._id}`}
            as={`/corp${Routes.CommunicationSetup}/${row._id}`}
          >
            <a target="_blank" rel="noopener noreferrer">
              <div className="truncate max-w-[500px]">{row.name}</div>
            </a>
          </Link>
        ),
        source: {
          id:
            row.onboardingFlowId ||
            row.greenhouseJobId ||
            row.csvCDSUrl ||
            row.workerTeamIds[0] ||
            row.projectIds[0],
          type: row.type,
          name: row.ocpFlowName || row.greenhouseJobName || row.name,
        },
        type: row.type,
        creator: row.creator
          .split(/[@+]/)[0]
          .replace('.', ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase()),
        pod: row.podGroup,
        status: row.activeDripCount,
        createAt: row.createdAt,
        metrics: row._id,
        history: (
          <div className="flex justify-center">
            <HistoryTableModal
              fetchEvents={async () => {
                const dripCampaignEvents = await getEmailDripCampaignEvents(row._id);
                const historyTableEvents = dripCampaignEvents.map(event => {
                  const prefix =
                    event.data.type === EmailDripEventType.EmailDripCampaign
                      ? 'campaign:'
                      : 'drip:';
                  const changesWithPrefix: any = {};
                  for (const key in event.data.changes) {
                    const prefixedKey = prefix + key;
                    changesWithPrefix[prefixedKey] = event.data.changes[key];
                  }
                  return {
                    user: event.user.email,
                    operation: event.operationType,
                    timestamp: event.timestamp,
                    changes: changesWithPrefix,
                  };
                });
                return historyTableEvents;
              }}
            />
          </div>
        ),
        actions: (
          <div className="flex-col whitespace-normal w-10vw min-h-10vh justify-center">
            <OptionsMenu
              direction="bottom-right"
              size="sm"
              icon="ellipsis-vertical"
              menuItems={
                row.commsType === CommsType.Campaign
                  ? [
                      {
                        label: 'Edit',
                        onClick: e => {
                          e.stopPropagation();
                          window.open(
                            `/corp${Routes.CommunicationSetup}/${row._id}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        },
                      },
                      {
                        label: 'Duplicate',
                        onClick: e => {
                          e.stopPropagation();
                          window.open(
                            `/corp${Routes.CommunicationSetup}/${
                              row._id
                            }?duplicate=${getDuplicateName(row.name, allData)}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        },
                      },
                      {
                        label: 'Pause',
                        onClick: e => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              `Are you sure you want to pause campaign [${row.name}]? This action will pause all the associated drips of this campaign.`,
                            )
                          ) {
                            pauseCampaignMutation.mutate(row._id);
                          }
                        },
                      },
                      {
                        label: 'Delete',
                        onClick: e => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              `Are you sure you want to delete campaign [${row.name}]? This action will delete all the associated drips of this campaign.`,
                            )
                          ) {
                            deleteCampaignMutation.mutate(row._id);
                          }
                        },
                      },
                    ]
                  : [
                      {
                        label: 'Duplicate',
                        onClick: e => {
                          e.stopPropagation();
                          window.open(
                            `/corp${Routes.CommunicationSetup}/${
                              row._id
                            }?duplicate=${getDuplicateName(row.name, allData)}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        },
                      },
                    ]
              }
              variant="neutral"
            />
          </div>
        ),
        commsType: row.commsType,
      }));
    }
  }, [
    currentTab,
    data,
    allData,
    pauseCampaignMutation,
    deleteCampaignMutation,
    lifecycleCommTemplates,
  ]);
  return (
    <div className="flex flex-col gap-4">
      <Table
        columns={
          currentTab === EmailDripCampaignListTab.LifecycleCommunications
            ? EmailDripCampaignEventDrivenCommunicationsColumns
            : EmailDripCampaignDataColumns
        }
        data={processedData}
        isLoading={isLoading}
        defaultPageSize={10}
      />
    </div>
  );
};
